import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { PhoneCall, Users, Megaphone, Workflow, FileBarChart, Languages, ChevronDown } from "lucide-react";
import Waveform from "@/components/Waveform";

const EASE = [0.22, 1, 0.36, 1];

function SampleTag() {
  return (
    <span className="ml-2 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-neutral-400">
      Sample
    </span>
  );
}

function Cell({ id, icon: Icon, eyebrow, title, desc, className = "", delay = 0, demo, cta, children }) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      id={id}
      data-testid={id ? `feature-${id}` : undefined}
      initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: reduce ? 0 : delay, ease: EASE }}
      whileHover={reduce ? {} : { y: -4 }}
      className={`group flex flex-col justify-between overflow-hidden rounded-3xl border border-neutral-200 bg-white p-7 transition-shadow hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.18)] ${className} scroll-mt-24`}
    >
      <div>
        <div className="flex items-center justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#111111] text-white">
            <Icon size={17} />
          </span>
          {demo && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              data-testid={id ? `feature-${id}-toggle` : undefined}
              className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-[11px] font-semibold text-neutral-500 transition-colors hover:border-[#111111] hover:text-[#111111]"
            >
              See it in action
              <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">{eyebrow}</p>
        <h3 className="mt-2 text-xl font-extrabold tracking-tight text-[#111111]">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-500">{desc}</p>
      </div>
      {children && <div className="mt-6">{children}</div>}
      {demo && open && (
        <div className="mt-5 rounded-2xl border border-neutral-200 bg-neutral-50/70 p-4" data-testid={id ? `feature-${id}-demo` : undefined}>
          {demo}
          {cta && (
            <Link
              to={cta.href}
              data-testid={id ? `feature-${id}-cta` : undefined}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#111111] underline underline-offset-4 hover:text-neutral-600"
            >
              {cta.label} →
            </Link>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function Features() {
  const reduce = useReducedMotion();
  return (
    <section id="features" data-testid="features-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">The platform</p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
            One system.
            <br />
            <span className="text-neutral-300">Every conversation.</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-neutral-500 sm:text-lg">
            Voice agents, CRM, marketing and reporting — built as one workspace,
            not five disconnected tools.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-4 lg:auto-rows-[215px] lg:grid-cols-6">
          <Cell
            id="voice"
            icon={PhoneCall}
            eyebrow="AI Voice Agents"
            title="Calls answered around the clock — once your line is connected"
            desc="Multilingual agents that greet, qualify and book — in English, Hindi or Telugu — with your greeting, your FAQs and your tone."
            className="lg:col-span-3 lg:row-span-2"
            delay={0}
            cta={{ href: "/signup", label: "Create an account to try the voice demo" }}
            demo={
              <div className="space-y-2 text-xs" data-testid="feature-voice-sample">
                <p className="font-semibold text-neutral-600">Sample conversation<SampleTag /></p>
                <p className="rounded-xl bg-white px-3 py-2 text-neutral-600"><span className="font-semibold text-neutral-400">Caller:</span> “Do you have anything free this Friday?”</p>
                <p className="rounded-xl bg-[#111111] px-3 py-2 text-white"><span className="font-semibold text-neutral-400">Agent:</span> “Yes — 11:30 or 15:00 with Priya. Shall I book one for you?”</p>
                <p className="text-[10px] text-neutral-400">Browser demo today · live phone answering activates when a telephony provider is connected.</p>
              </div>
            }
          >
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
              <Waveform compact />
              <p className="mt-3 text-center text-[11px] text-neutral-400">
                Try the browser voice demo after sign-up · phone answering activates when a telephony provider is connected
              </p>
            </div>
          </Cell>

          <Cell
            id="crm"
            icon={Users}
            eyebrow="CRM"
            title="Leads that never slip through"
            desc="Every enquiry, call and follow-up on one timeline — with consent and do-not-call respected automatically."
            className="lg:col-span-3"
            delay={0.08}
            cta={{ href: "/signup", label: "Start your CRM" }}
            demo={
              <div className="space-y-1.5 text-xs" data-testid="feature-crm-sample">
                <p className="font-semibold text-neutral-600">Sample pipeline<SampleTag /></p>
                {[["Anita Rao", "New", "Landing page"], ["James Wright", "Booked", "Google Ads"], ["Priya Shah", "Converted", "CSV import"]].map(([n, s, src]) => (
                  <div key={n} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5">
                    <span className="font-medium text-neutral-700">{n}</span>
                    <span className="text-neutral-400">{src}</span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-600">{s}</span>
                  </div>
                ))}
              </div>
            }
          >
            <div className="space-y-2">
              {[100, 72, 88].map((w, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-neutral-200" />
                  <span className="h-2 rounded-full bg-neutral-200" style={{ width: `${w}%` }} />
                </div>
              ))}
            </div>
          </Cell>

          <Cell
            id="marketing"
            icon={Megaphone}
            eyebrow="AI Marketing"
            title="Campaigns drafted in seconds"
            desc="Brief the AI, review the copy, schedule the send — social, email and ads from one studio."
            className="lg:col-span-3"
            delay={0.16}
            cta={{ href: "/signup", label: "Open the marketing studio" }}
            demo={
              <div className="space-y-2 text-xs" data-testid="feature-marketing-sample">
                <p className="font-semibold text-neutral-600">Sample draft<SampleTag /></p>
                <p className="rounded-xl bg-white px-3 py-2 text-neutral-600">“Autumn openings this week — book a Saturday consultation before they go.”</p>
                <p className="flex items-center gap-2 text-[10px] text-neutral-400">
                  <span className="rounded-full border border-neutral-300 px-2 py-0.5 font-semibold">Awaiting your approval</span>
                  Nothing publishes or sends without you.
                </p>
              </div>
            }
          />

          <Cell
            id="automations"
            icon={Workflow}
            eyebrow="Automations"
            title="Follow-ups on autopilot"
            desc="A lead with a follow-up date becomes a task — automatically."
            className="lg:col-span-2"
            delay={0.1}
            cta={{ href: "/signup", label: "Set up automations" }}
            demo={
              <p className="text-xs leading-relaxed text-neutral-600" data-testid="feature-automations-sample">
                <span className="font-semibold text-neutral-600">How it works<SampleTag /></span><br />
                When a lead gets a follow-up date, a task appears on your dashboard that morning. No messages are ever sent without your approval.
              </p>
            }
          />

          <Cell
            id="reports"
            icon={FileBarChart}
            eyebrow="Reports"
            title="Branded, printable truth"
            desc="Real counts only — no invented metrics, ever."
            className="lg:col-span-2"
            delay={0.18}
            cta={{ href: "/signup", label: "See your real numbers" }}
            demo={
              <p className="text-xs leading-relaxed text-neutral-600" data-testid="feature-reports-sample">
                <span className="font-semibold text-neutral-600">Sample line<SampleTag /></span><br />
                “42 leads on record, 6 new this week — a stable pipeline. 3 follow-ups are still open.” Download as a branded PDF.
              </p>
            }
          />

          <Cell
            id="multilingual"
            icon={Languages}
            eyebrow="Multilingual"
            title="తెలుగు · हिन्दी · English"
            desc="Detects the caller's language and switches with them."
            className="lg:col-span-2"
            delay={0.26}
            cta={{ href: "/signup", label: "Try a multilingual demo" }}
            demo={
              <div className="space-y-1.5 text-xs text-neutral-600" data-testid="feature-multilingual-sample">
                <p className="font-semibold text-neutral-600">Sample greetings<SampleTag /></p>
                <p>“How can I help you today?”</p>
                <p>“మీకు ఎలా సహాయపడగలను?”</p>
                <p>“मैं आपकी कैसे मदद कर सकता हूँ?”</p>
              </div>
            }
          />
        </div>
      </div>
    </section>
  );
}
